// Deterministic organic-insights derivations over the loaded SeoReport. These
// power both the ranked Insights tables and the compact JSON payload sent to the
// AI action-brief endpoint. Pure functions — no React, no fetching.
//
// All rollups reuse the impression-weighted helpers in seoCompute.ts so numbers
// reconcile with the Summary / page-group tables.

import { groupSum, groupPrevSum, pagePrevMetric, queryPrevImpr, isMtdMonth } from './seoCompute';
import { PageGroup, PageRow, QueryRow, SeoReport } from './seoTypes';

const BASE = 'https://www.cleocremation.com';

/** Best-effort live URL for a page row label (e.g. "/locations/laval [fr]"). */
export function pageUrl(label: string): string | null {
    const fr = label.includes('[fr]');
    const core = label.replace(' [fr]', '').trim();
    let path: string | null = null;
    if (core === 'Home') path = '/';
    else if (core === 'Direct Cremation') path = '/direct-cremation';
    else if (core.startsWith('/')) path = core;
    if (path == null) return null;
    if (path === '/') return fr ? `${BASE}/fr` : BASE;
    return `${BASE}${fr ? '/fr' : ''}${path}`;
}

/** Whole months between an ISO date and now (null when no lastmod). */
export function ageMonths(lastmod: string | null): number | null {
    if (!lastmod) return null;
    const t = Date.parse(lastmod);
    if (Number.isNaN(t)) return null;
    const days = (Date.now() - t) / 86_400_000;
    return Math.max(0, Math.round(days / 30.44));
}

const ctrOf = (clicks: number, impr: number): number => (impr ? clicks / impr : 0);

// ---- Top movers (pages) ----

export interface PageMover {
    label: string;
    group: PageGroup;
    url: string | null;
    impr: number;
    imprPrev: number;
    clicks: number;
    clicksPrev: number;
    pos: number;
    posPrev: number;
    series: number[];
}

/** Flatten every group's pages (excludes the "All pages" totals row). */
function allPages(report: SeoReport): Array<{ p: PageRow; group: PageGroup }> {
    const out: Array<{ p: PageRow; group: PageGroup }> = [];
    for (const g of report.groupOrder) {
        for (const p of report.groups[g].pages) out.push({ p, group: g });
    }
    return out;
}

function toMover(report: SeoReport, p: PageRow, group: PageGroup, sel: number): PageMover {
    return {
        label: p.label,
        group,
        url: pageUrl(p.label),
        impr: p.impr[sel] ?? 0,
        imprPrev: pagePrevMetric(report, p, 'impr', sel) ?? 0,
        clicks: p.clicks[sel] ?? 0,
        clicksPrev: pagePrevMetric(report, p, 'clicks', sel) ?? 0,
        pos: p.pos[sel] ?? 0,
        posPrev: pagePrevMetric(report, p, 'position', sel) ?? 0,
        series: p.impr,
    };
}

// Clicks (lead-proximate) drive the ranking; pages with no click movement fall
// back to impression change so pure-visibility movers still surface. The ×1e5
// keeps any real click delta ranked above impression-only deltas.
const moverScore = (m: PageMover): number => {
    const dc = m.clicks - m.clicksPrev;
    return dc !== 0 ? dc * 100_000 : m.impr - m.imprPrev;
};

export function topMovers(report: SeoReport, sel: number, n = 6): { up: PageMover[]; down: PageMover[] } {
    if (sel <= 0) return { up: [], down: [] };
    const movers = allPages(report)
        .map(({ p, group }) => toMover(report, p, group, sel))
        // Cut noise: ignore pages with negligible visibility either month.
        .filter((m) => Math.max(m.impr, m.imprPrev) >= 30 && moverScore(m) !== 0);
    const sorted = [...movers].sort((a, b) => moverScore(b) - moverScore(a));
    const up = sorted.filter((m) => moverScore(m) > 0).slice(0, n);
    const down = sorted
        .filter((m) => moverScore(m) < 0)
        .slice(-n)
        .reverse();
    return { up, down };
}

// ---- Rewrite candidates (stale + underperforming pages) ----

export interface RewriteCandidate {
    label: string;
    group: PageGroup;
    url: string | null;
    impr: number;
    pos: number;
    ctr: number;
    ageMonths: number | null;
    reasons: string[];
    score: number;
    series: number[];
}

export function rewriteCandidates(report: SeoReport, sel: number, n = 8): RewriteCandidate[] {
    const out: RewriteCandidate[] = [];
    for (const { p, group } of allPages(report)) {
        // Obituaries are memorial pages, not refreshable lead content — never a rewrite target.
        if (group === 'Obituaries') continue;
        const impr = p.impr[sel] ?? 0;
        if (impr < 50) continue; // needs enough traffic to be worth a rewrite
        const pos = p.pos[sel] ?? 0;
        const ctr = ctrOf(p.clicks[sel] ?? 0, impr);
        const age = ageMonths(p.lastmod);

        // Position penalty: impressions stuck off the top of page 1 are "wasted".
        const posPenalty = pos > 10 ? 1 : pos > 5 ? 0.5 : pos > 3 ? 0.2 : 0;
        // Decay: average position got materially worse vs 3 months ago.
        const pos3 = sel >= 3 ? p.pos[sel - 3] ?? 0 : 0;
        const decayed = pos3 > 0 && pos - pos3 > 1;
        // Title/meta problem: ranks decently but few clicks.
        const lowCtr = pos > 0 && pos <= 10 && impr >= 200 && ctr < 0.01;
        const stale = age == null ? 0 : Math.min(1, age / 12);

        const score = impr * (posPenalty + (decayed ? 0.5 : 0) + (lowCtr ? 0.3 : 0)) * (1 + stale);
        if (score <= 0) continue;

        const reasons: string[] = [];
        if (age != null && age >= 6) reasons.push(`stale ${age}mo`);
        if (pos > 10) reasons.push('page 2+ rank');
        else if (pos > 5) reasons.push('below the fold');
        if (decayed) reasons.push('position ▼');
        if (lowCtr) reasons.push('low CTR');
        if (!reasons.length) continue; // score-only with no nameable reason → skip

        out.push({ label: p.label, group, url: pageUrl(p.label), impr, pos, ctr, ageMonths: age, reasons, score, series: p.impr });
    }
    return out.sort((a, b) => b.score - a.score).slice(0, n);
}

// ---- Keyword optimization gaps (queries ranking on page 1-bottom / page 2-3) ----

export interface KeywordGap {
    q: string;
    theme: string;
    page: string;
    impr: number;
    imprPrev: number;
    ctr: number;
    pos: number;
    score: number;
    series: number[];
}

function allQueries(report: SeoReport): QueryRow[] {
    return report.queries.flatMap((b) => b.rows);
}

export function keywordGaps(report: SeoReport, sel: number, n = 10): KeywordGap[] {
    const out: KeywordGap[] = [];
    for (const r of allQueries(report)) {
        const impr = r.impr[sel] ?? 0;
        const pos = r.pos[sel] ?? 0;
        if (impr < 50 || pos < 5 || pos > 20) continue; // the "almost ranking" band
        const ctr = ctrOf(r.clicks[sel] ?? 0, impr);
        // Biggest opportunity sits just off page 1 (pos ~6-15): score peaks there.
        const opportunity = pos <= 15 ? 1 : 0.5;
        const score = impr * opportunity;
        out.push({
            q: r.q,
            theme: r.theme,
            page: r.page,
            impr,
            imprPrev: queryPrevImpr(report, r, sel) ?? 0,
            ctr,
            pos,
            score,
            series: r.impr,
        });
    }
    return out.sort((a, b) => b.score - a.score).slice(0, n);
}

// ---- Paid-search moves (ad candidates + cannibalization) ----

export interface AdMove {
    q: string;
    theme: string;
    impr: number;
    pos: number;
    paid: boolean;
    paidCost: number;
    score: number;
}

export function adMoves(
    report: SeoReport,
    sel: number,
    n = 8,
): { candidates: AdMove[]; cannibalization: AdMove[] } {
    const highIntent = report.queries.find((b) => b.name === 'High-intent');
    const rows = highIntent ? highIntent.rows : [];
    const candidates: AdMove[] = [];
    const cannibalization: AdMove[] = [];
    for (const r of rows) {
        const impr = r.impr[sel] ?? 0;
        if (impr < 30) continue;
        const pos = r.pos[sel] ?? 0;
        const paid = !!r.paid;
        const paidCost = r.paidCost ?? 0;
        const move: AdMove = { q: r.q, theme: r.theme, impr, pos, paid, paidCost, score: 0 };
        // Ad candidate: real intent volume where organic ranks weakly and we don't
        // already bid meaningfully on it.
        if (pos > 5 && (!paid || paidCost < 5)) {
            candidates.push({ ...move, score: impr * (pos / 10) });
        }
        // Cannibalization: paying while already ranking #1-2 organically.
        if (paid && pos > 0 && pos <= 2) {
            cannibalization.push({ ...move, score: paidCost });
        }
    }
    return {
        candidates: candidates.sort((a, b) => b.score - a.score).slice(0, n),
        cannibalization: cannibalization.sort((a, b) => b.score - a.score).slice(0, n),
    };
}

// ---- Compact payload for the AI action brief ----

function siteRollup(report: SeoReport, sel: number) {
    const t = report.totals;
    // pmtd-aware previous baseline (same-period last month for the partial month).
    const pv = (key: 'impr' | 'clicks' | 'position' | 'fills' | 'phone' | 'sessions') => pagePrevMetric(report, t, key, sel) ?? 0;
    return {
        impr: t.impr[sel] ?? 0,
        imprPrev: pv('impr'),
        clicks: t.clicks[sel] ?? 0,
        clicksPrev: pv('clicks'),
        ctr: +(ctrOf(t.clicks[sel] ?? 0, t.impr[sel] ?? 0) * 100).toFixed(2),
        position: t.pos[sel] ?? 0,
        positionPrev: pv('position'),
        visits: t.sessions?.[sel] ?? 0,
        visitsPrev: pv('sessions'),
        fills: t.fills[sel] ?? 0,
        fillsPrev: pv('fills'),
        phone: t.phone[sel] ?? 0,
        phonePrev: pv('phone'),
    };
}

function groupRollups(report: SeoReport, sel: number) {
    return report.groupOrder.map((g) => {
        const cur = groupSum(report.groups[g], sel);
        const p = groupPrevSum(report, report.groups[g], sel);
        return {
            group: g,
            impr: cur.impr,
            imprPrev: p?.impr ?? 0,
            clicks: cur.clicks,
            clicksPrev: p?.clicks ?? 0,
            position: +cur.position.toFixed(1),
            visits: cur.sessions,
            visitsPrev: p?.sessions ?? 0,
            fills: cur.fills,
            fillsPrev: p?.fills ?? 0,
            phone: cur.phone,
        };
    });
}

/** Assemble the compact JSON the insights endpoint feeds to Claude. */
export function buildInsightPayload(report: SeoReport, sel: number): Record<string, unknown> {
    const prev = sel - 1;
    const movers = topMovers(report, sel);
    const ads = adMoves(report, sel);
    const trimMover = (m: PageMover) => ({
        page: m.label,
        group: m.group,
        impr: m.impr,
        imprPrev: m.imprPrev,
        clicks: m.clicks,
        clicksPrev: m.clicksPrev,
        pos: +m.pos.toFixed(1),
        posPrev: +m.posPrev.toFixed(1),
    });
    const mtd = isMtdMonth(report, sel);
    return {
        month: report.months[sel],
        priorMonth: prev >= 0 ? report.months[prev] : null,
        // All *Prev figures use this baseline. For the in-progress month that's the
        // prior month clipped to the same day count (apples-to-apples), not full month.
        comparisonBasis: mtd && report.meta.priorMtdLabel
            ? `same period last month (${report.meta.priorMtdLabel}), month-to-date`
            : 'full prior month',
        ads: report.ads,
        site: siteRollup(report, sel),
        groups: groupRollups(report, sel),
        topMovers: { up: movers.up.map(trimMover), down: movers.down.map(trimMover) },
        rewriteCandidates: rewriteCandidates(report, sel).map((r) => ({
            page: r.label,
            group: r.group,
            impr: r.impr,
            pos: +r.pos.toFixed(1),
            ctrPct: +(r.ctr * 100).toFixed(2),
            ageMonths: r.ageMonths,
            reasons: r.reasons,
        })),
        keywordGaps: keywordGaps(report, sel).map((k) => ({
            query: k.q,
            theme: k.theme,
            landingPage: k.page,
            impr: k.impr,
            pos: +k.pos.toFixed(1),
            ctrPct: +(k.ctr * 100).toFixed(2),
        })),
        adCandidates: ads.candidates.map((a) => ({
            query: a.q,
            theme: a.theme,
            impr: a.impr,
            organicPos: +a.pos.toFixed(1),
            currentlyPaid: a.paid,
            paidCost: a.paidCost,
        })),
        cannibalization: ads.cannibalization.map((a) => ({
            query: a.q,
            impr: a.impr,
            organicPos: +a.pos.toFixed(1),
            paidCost: a.paidCost,
        })),
    };
}
